import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';

const formatMoney = (value, currency = 'CLP') => {
  const code = ['CLP', 'USD', 'EUR', 'UF'].includes(String(currency || '').toUpperCase())
    ? String(currency).toUpperCase() === 'UF'
      ? 'CLP'
      : String(currency).toUpperCase()
    : 'CLP';
  const prefix = String(currency || 'CLP').toUpperCase() === 'UF' ? 'UF ' : '';
  const formatted = new Intl.NumberFormat('es-CL', {
    style: String(currency || '').toUpperCase() === 'UF' ? 'decimal' : 'currency',
    currency: code,
    maximumFractionDigits: code === 'CLP' ? 0 : 2,
  }).format(Number(value || 0));
  return prefix ? `${prefix}${formatted}` : formatted;
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const toDateInput = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

const Field = ({ label, value, muted, text }) => (
  <div style={{ minWidth: 0, lineHeight: 1.25 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: muted, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: text, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

/**
 * Modal de facturación: muestra datos de la OC y permite folio + fechas por ítem.
 */
const PurchaseOrderInvoicingModal = ({
  isOpen,
  onClose,
  purchaseOrder,
  isDarkMode = false,
  isSaving = false,
  onSave,
}) => {
  const ui = useUIColors();
  const [lines, setLines] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !purchaseOrder) return;
    const items = Array.isArray(purchaseOrder.items) ? purchaseOrder.items : [];
    setLines(
      items.map((item) => ({
        id: item.id,
        item_type: item.item_type || '',
        item_code: item.item_code || '',
        description: item.description || '',
        quantity: item.quantity,
        unit: item.unit || 'UN',
        unit_price: item.unit_price,
        line_total: item.line_total,
        invoice_folio: item.invoice_folio || '',
        invoice_issue_date: toDateInput(item.invoice_issue_date),
        invoice_due_date: toDateInput(item.invoice_due_date),
      }))
    );
    setError('');
  }, [isOpen, purchaseOrder]);

  const currency = String(purchaseOrder?.currency || 'CLP').toUpperCase();

  const inputStyle = useMemo(
    () => ({
      width: '100%',
      height: 34,
      padding: '0 8px',
      borderRadius: 8,
      border: `1px solid ${ui.inputBorder}`,
      background: ui.inputBg,
      color: ui.text,
      fontSize: 12,
      outline: 'none',
      boxSizing: 'border-box',
    }),
    [ui]
  );

  if (!purchaseOrder) return null;

  const updateLine = (index, patch) => {
    setLines((prev) => prev.map((line, idx) => (idx === index ? { ...line, ...patch } : line)));
  };

  const handleSave = async () => {
    setError('');
    try {
      await onSave?.({
        items: lines.map((line) => ({
          id: line.id,
          invoice_folio: String(line.invoice_folio || '').trim(),
          invoice_issue_date: line.invoice_issue_date || null,
          invoice_due_date: line.invoice_due_date || null,
        })),
      });
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la facturación.');
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => !isSaving && onClose?.()}
      title={`Facturación · ${purchaseOrder.po_code || 'OC'}`}
      subtitle="Asigna folio y fechas de factura por ítem"
      isDarkMode={isDarkMode}
      size="xxl"
    >
      <div
        style={{
          padding: '12px 16px 16px',
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 140px)',
          background: ui.page,
        }}
      >
        <div
          style={{
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            background: ui.panel,
            padding: '12px 14px',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: '10px 16px',
            }}
          >
            <Field label="Proveedor" value={purchaseOrder.supplier_name} muted={ui.muted} text={ui.text} />
            <Field label="RUT" value={purchaseOrder.supplier_rut} muted={ui.muted} text={ui.text} />
            <Field label="Fecha OC" value={formatDate(purchaseOrder.order_date)} muted={ui.muted} text={ui.text} />
            <Field label="Estado" value={purchaseOrder.status} muted={ui.muted} text={ui.text} />
            <Field label="Tipo documento" value={purchaseOrder.document_type} muted={ui.muted} text={ui.text} />
            <Field label="Moneda" value={currency} muted={ui.muted} text={ui.text} />
            <Field
              label="Condición de pago"
              value={purchaseOrder.payment_terms}
              muted={ui.muted}
              text={ui.text}
            />
            <Field
              label="Total"
              value={formatMoney(purchaseOrder.total_amount, 'CLP')}
              muted={ui.muted}
              text={ui.text}
            />
          </div>
        </div>

        <div
          style={{
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            overflow: 'hidden',
            background: ui.panel,
          }}
        >
          <div
            style={{
              padding: '10px 12px',
              borderBottom: `1px solid ${ui.border}`,
              background: ui.header,
              fontSize: 13,
              fontWeight: 700,
              color: ui.text,
            }}
          >
            Ítems a facturar
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead>
                <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
                  {[
                    'Tipo',
                    'Código',
                    'Descripción',
                    'Cant.',
                    'P. unit.',
                    'Total',
                    'Folio factura',
                    'Emisión factura',
                    'Vencimiento',
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        padding: '8px 10px',
                        textAlign: 'left',
                        fontSize: 11,
                        fontWeight: 700,
                        color: ui.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 20, textAlign: 'center', color: ui.muted }}>
                      Esta OC no tiene ítems.
                    </td>
                  </tr>
                ) : (
                  lines.map((line, index) => (
                    <tr key={line.id || index} style={{ borderBottom: `1px solid ${ui.border}` }}>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: ui.text }}>
                        {line.item_type || '—'}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: ui.muted }}>
                        {line.item_code || '—'}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: ui.text, maxWidth: 220 }}>
                        {line.description || '—'}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                        {line.quantity} {line.unit}
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                        {formatMoney(line.unit_price, currency)}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          fontSize: 12,
                          fontWeight: 700,
                          color: ui.text,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatMoney(line.line_total, currency)}
                      </td>
                      <td style={{ padding: '8px 10px', minWidth: 130 }}>
                        <input
                          style={inputStyle}
                          value={line.invoice_folio}
                          onChange={(e) => updateLine(index, { invoice_folio: e.target.value })}
                          placeholder="Folio"
                          disabled={isSaving}
                        />
                      </td>
                      <td style={{ padding: '8px 10px', minWidth: 140 }}>
                        <input
                          type="date"
                          style={inputStyle}
                          value={line.invoice_issue_date}
                          onChange={(e) => updateLine(index, { invoice_issue_date: e.target.value })}
                          disabled={isSaving}
                        />
                      </td>
                      <td style={{ padding: '8px 10px', minWidth: 140 }}>
                        <input
                          type="date"
                          style={inputStyle}
                          value={line.invoice_due_date}
                          onChange={(e) => updateLine(index, { invoice_due_date: e.target.value })}
                          disabled={isSaving}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {error ? (
          <p style={{ margin: '10px 0 0', color: '#dc2626', fontSize: 13 }}>{error}</p>
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 14,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder}`,
              background: 'transparent',
              color: ui.text,
              fontWeight: 600,
              fontSize: 13,
              cursor: isSaving ? 'wait' : 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || lines.length === 0}
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#0f766e',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: isSaving || lines.length === 0 ? 'not-allowed' : 'pointer',
              opacity: isSaving || lines.length === 0 ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Guardando…' : 'Guardar facturación'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PurchaseOrderInvoicingModal;
